import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getAuthToken } from '../utils/auth';
import BASE_URL from '../utils/api';

// Erro devolvido pelo servidor (ex.: CPF inválido): mostra a mensagem e não tenta de novo
class ErroServidor extends Error {}

// ── CPF / CNPJ de quem aceita o orçamento ──
export const somenteDigitos = (v) => (v || '').replace(/\D/g, '');

export const mascaraCpf = (v) => somenteDigitos(v).slice(0, 11)
  .replace(/(\d{3})(\d)/, '$1.$2')
  .replace(/(\d{3})(\d)/, '$1.$2')
  .replace(/(\d{3})(\d{1,2})$/, '$1-$2');

export const mascaraCnpj = (v) => somenteDigitos(v).slice(0, 14)
  .replace(/^(\d{2})(\d)/, '$1.$2')
  .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
  .replace(/\.(\d{3})(\d)/, '.$1/$2')
  .replace(/(\d{4})(\d)/, '$1-$2');

export const cpfValido = (valor) => {
  const cpf = somenteDigitos(valor);
  if (cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) return false;
  const digito = (tamanho) => {
    let soma = 0;
    for (let i = 0; i < tamanho; i++) soma += Number(cpf[i]) * (tamanho + 1 - i);
    const resto = soma % 11;
    return resto < 2 ? 0 : 11 - resto;
  };
  return digito(9) === Number(cpf[9]) && digito(10) === Number(cpf[10]);
};

export const cnpjValido = (valor) => {
  const cnpj = somenteDigitos(valor);
  if (cnpj.length !== 14 || /^(\d)\1+$/.test(cnpj)) return false;
  const digito = (tamanho) => {
    const pesos = tamanho === 12 ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    let soma = 0;
    for (let i = 0; i < tamanho; i++) soma += Number(cnpj[i]) * pesos[i];
    const resto = soma % 11;
    return resto < 2 ? 0 : 11 - resto;
  };
  return digito(12) === Number(cnpj[12]) && digito(13) === Number(cnpj[13]);
};

// Página pública: CPF mascarado na confirmação (mesmo formato do backend)
const cpfMascaradoPublico = (valor) => {
  const cpf = somenteDigitos(valor);
  return cpf.length === 11 ? `***.${cpf.slice(3, 6)}.${cpf.slice(6, 9)}-**` : '';
};

// Função utilitária para chamadas da API com fallback
const apiCall = async (endpoint, method = 'GET', data = null) => {
  const urls = [
    `${BASE_URL}/${endpoint}`,
    `${BASE_URL}/${endpoint}`,
    `${BASE_URL}/${endpoint}`,
    `${BASE_URL}/${endpoint}`,
    `${BASE_URL}/${endpoint}`
  ];

  for (const url of urls) {
    try {
      const config = {
        method,
        headers: { 'Content-Type': 'application/json' }
      };
      const token = getAuthToken();
      if (token) config.headers['Authorization'] = `Bearer ${token}`;
      if (data && (method === 'POST' || method === 'PUT')) {
        config.body = JSON.stringify(data);
      }
      const response = await fetch(url, config);
      if (response.ok || (response.status >= 200 && response.status < 300)) {
        if (method === 'DELETE' || response.status === 204) {
          return { success: true, status: response.status };
        }
        const contentType = response.headers.get('Content-Type') || '';
        if (contentType.includes('application/json')) {
          return await response.json();
        }
        return { success: true, status: response.status };
      }
      let mensagem = `Erro ${response.status}`;
      try {
        const corpo = await response.json();
        mensagem = corpo.erro || corpo.mensagem || corpo.message || mensagem;
      } catch { /* resposta sem JSON */ }
      throw new ErroServidor(mensagem);
    } catch (error) {
      if (error instanceof ErroServidor) throw error;
      // try next URL
    }
  }
  throw new Error('Não foi possível conectar ao servidor.');
};

export default function AprovacaoOrcamento() {
  const { id: idParam } = useParams();
  const params = new URLSearchParams(window.location.search);
  const id = idParam || params.get('orcamentoId') || '';
  const tenantCnpj =
    params.get('tenantCnpj') ||
    params.get('tenant') ||
    params.get('cnpj') ||
    '';
  const [orcamento, setOrcamento] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Identificação de quem aceita: CPF (pessoa física) ou CNPJ da empresa + CPF do responsável (pessoa jurídica)
  const [cpf, setCpf] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [descricaoAceite, setDescricaoAceite] = useState('');
  const [aceito, setAceito] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [confirmado, setConfirmado] = useState(false);

  useEffect(() => {
    carregarOrcamento();
  }, [id]);

  const carregarOrcamento = async () => {
    try {
      if (!id) {
        setError('Link inválido: orçamento não informado.');
        return;
      }
      setLoading(true);
      setError('');
      const tenantQuery = tenantCnpj ? `?tenantCnpj=${encodeURIComponent(tenantCnpj)}` : '';
      const data = await apiCall(`AceiteOrcamento/publico/${id}${tenantQuery}`);
      setOrcamento(data);

      // Se já foi aceito, mostrar estado de confirmado
      if (data.jaAceito) {
        setConfirmado(true);
        setDescricaoAceite(data.nomeAceite || '');
      }
    } catch (err) {
      setError('Não foi possível carregar os dados do orçamento. Verifique o link e tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const pessoaJuridica = orcamento?.tipoPessoaCliente === 'PJ';
  const documentosValidos = cpfValido(cpf) && (!pessoaJuridica || cnpjValido(cnpj));

  const handleConfirmar = async () => {
    if (pessoaJuridica && !cnpjValido(cnpj)) {
      alert('Por favor, informe um CNPJ válido da empresa.');
      return;
    }
    if (!cpfValido(cpf)) {
      alert(pessoaJuridica ? 'Por favor, informe um CPF válido do responsável pela empresa.' : 'Por favor, informe um CPF válido.');
      return;
    }
    if (!aceito) {
      alert('Você precisa aceitar as condições do orçamento para confirmar.');
      return;
    }

    try {
      setEnviando(true);
      const endpoint = tenantCnpj
        ? `AceiteOrcamento?tenantCnpj=${encodeURIComponent(tenantCnpj)}`
        : 'AceiteOrcamento';

      await apiCall(endpoint, 'POST', {
        Cpf: somenteDigitos(cpf),
        Cnpj: pessoaJuridica ? somenteDigitos(cnpj) : null,
        OrcamentoId: parseInt(id),
        Aceito: true,
        TenantCnpj: tenantCnpj || null
      });
      setDescricaoAceite(pessoaJuridica
        ? `CPF ${cpfMascaradoPublico(cpf)}, representando o CNPJ ${mascaraCnpj(cnpj)}`
        : `CPF ${cpfMascaradoPublico(cpf)}`);
      setConfirmado(true);
    } catch (err) {
      alert(err instanceof ErroServidor ? err.message : 'Erro ao confirmar aceite. Tente novamente.');
    } finally {
      setEnviando(false);
    }
  };

  const formatarData = (data) => {
    if (!data) return '-';
    try {
      return new Date(data).toLocaleDateString('pt-BR');
    } catch {
      return data;
    }
  };

  const formatarValor = (valor) => {
    if (!valor && valor !== 0) return 'R$ 0,00';
    return `R$ ${parseFloat(valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.loadingContainer}>
            <div style={styles.spinner}></div>
            <p style={{ color: '#666', marginTop: 16 }}>Carregando orçamento...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.errorContainer}>
            <span style={{ fontSize: 48, marginBottom: 16 }}>⚠️</span>
            <h2 style={{ color: '#d32f2f', marginBottom: 8 }}>Orçamento não encontrado</h2>
            <p style={{ color: '#666' }}>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (confirmado) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.successContainer}>
            <span style={{ fontSize: 64, marginBottom: 16 }}>✅</span>
            <h2 style={{ color: '#2e7d32', marginBottom: 8 }}>Orçamento Aceito!</h2>
            <p style={{ color: '#555', fontSize: 16, textAlign: 'center' }}>
              O orçamento <strong>#{orcamento?.orcamentoId}</strong> foi aceito com sucesso por <strong>{descricaoAceite || orcamento?.nomeAceite}</strong>.
            </p>
            {orcamento?.dataAceite && (
              <p style={{ color: '#888', fontSize: 14, marginTop: 8 }}>
                Data de aceite: {formatarData(orcamento.dataAceite)}
              </p>
            )}
            <p style={{ color: '#888', fontSize: 14, marginTop: 16 }}>
              Você pode fechar esta página.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Cabeçalho */}
        <div style={styles.header}>
          <h1 style={styles.headerTitle}>Aprovação de Orçamento</h1>
          <p style={styles.headerSubtitle}>Orçamento #{orcamento?.orcamentoId}</p>
        </div>

        {/* Informações do Orçamento */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Dados do Orçamento</h3>
          
          <div style={styles.infoGrid}>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Cliente:</span>
              <span style={styles.infoValue}>{orcamento?.nomeCliente || '-'}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Data de Criação:</span>
              <span style={styles.infoValue}>{formatarData(orcamento?.dataCriacao)}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Validade:</span>
              <span style={styles.infoValue}>{formatarData(orcamento?.validadeOrcamento)}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Prazo de Garantia:</span>
              <span style={styles.infoValue}>{orcamento?.prazoGarantia ? `${orcamento.prazoGarantia} meses` : '-'}</span>
            </div>
            {orcamento?.codigoOrcamentoPDF && (
              <div style={styles.infoItem}>
                <span style={styles.infoLabel}>Código:</span>
                <span style={styles.infoValue}>{orcamento.codigoOrcamentoPDF}</span>
              </div>
            )}
          </div>

          {orcamento?.descricaoOrcamento && (
            <div style={styles.descricaoBox}>
              <strong>Descrição:</strong>
              <p style={{ margin: '8px 0 0', whiteSpace: 'pre-wrap' }}>{orcamento.descricaoOrcamento}</p>
            </div>
          )}
        </div>

        {/* Serviços */}
        {orcamento?.servicos && orcamento.servicos.length > 0 && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Serviços</h3>
            <div style={styles.tabelaContainer}>
              <table style={styles.tabela}>
                <thead>
                  <tr>
                    <th style={styles.th}>#</th>
                    <th style={styles.th}>Título</th>
                    <th style={styles.th}>Descrição</th>
                  </tr>
                </thead>
                <tbody>
                  {orcamento.servicos.map((servico, index) => (
                    <tr key={index} style={index % 2 === 0 ? styles.trEven : {}}>
                      <td style={styles.td}>{index + 1}</td>
                      <td style={styles.td}>{servico.titulo || '-'}</td>
                      <td style={styles.td}>{servico.descricao || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Valores */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Valores</h3>
          <div style={styles.valoresBox}>
            {orcamento?.desconto > 0 && (
              <div style={styles.valorLinha}>
                <span>Desconto:</span>
                <span style={{ color: '#d32f2f' }}>- {formatarValor(orcamento.desconto)}</span>
              </div>
            )}
            <div style={{ ...styles.valorLinha, ...styles.valorTotal }}>
              <span>Valor Total:</span>
              <span style={{ color: '#2e7d32' }}>{formatarValor(orcamento?.precoFinal ?? orcamento?.valor)}</span>
            </div>
          </div>
        </div>

        {/* Condições de Pagamento */}
        {orcamento?.condicoes && orcamento.condicoes.length > 0 && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Condições de Pagamento</h3>
            <ul style={styles.condicoesList}>
              {orcamento.condicoes.map((condicao, index) => (
                <li key={index} style={styles.condicaoItem}>
                  {condicao.descricao}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Separador */}
        <hr style={styles.separador} />

        {/* Seção de Aceite */}
        <div style={styles.aceiteSection}>
          <h3 style={styles.aceiteTitulo}>Confirmação de Aceite</h3>
          
          {/* Pessoa jurídica: CNPJ da empresa + CPF de quem aceita em nome dela */}
          {pessoaJuridica && (
            <div style={styles.campoNome}>
              <label style={styles.label}>CNPJ da empresa:</label>
              <input
                type="text"
                inputMode="numeric"
                value={cnpj}
                onChange={(e) => setCnpj(mascaraCnpj(e.target.value))}
                placeholder="00.000.000/0000-00"
                style={styles.input}
                disabled={enviando}
              />
              {somenteDigitos(cnpj).length === 14 && !cnpjValido(cnpj) && (
                <span style={{ color: '#d32f2f', fontSize: 13 }}>CNPJ inválido</span>
              )}
            </div>
          )}

          <div style={styles.campoNome}>
            <label style={styles.label}>{pessoaJuridica ? 'CPF do responsável pela empresa:' : 'CPF:'}</label>
            <input
              type="text"
              inputMode="numeric"
              value={cpf}
              onChange={(e) => setCpf(mascaraCpf(e.target.value))}
              placeholder="000.000.000-00"
              style={styles.input}
              disabled={enviando}
            />
            {somenteDigitos(cpf).length === 11 && !cpfValido(cpf) && (
              <span style={{ color: '#d32f2f', fontSize: 13 }}>CPF inválido</span>
            )}
          </div>

          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={aceito}
              onChange={(e) => setAceito(e.target.checked)}
              style={styles.checkbox}
              disabled={enviando}
            />
            <span style={styles.checkboxText}>
              Eu, portador(a) do CPF <strong>{cpf || '(informe seu CPF)'}</strong>
              {pessoaJuridica && (
                <>, representante da empresa inscrita no CNPJ <strong>{cnpj || '(informe o CNPJ)'}</strong></>
              )}
              , declaro que li e aceito todas as condições descritas neste orçamento.
            </span>
          </label>

          <button
            onClick={handleConfirmar}
            disabled={!aceito || !documentosValidos || enviando}
            style={{
              ...styles.botaoConfirmar,
              opacity: (!aceito || !documentosValidos || enviando) ? 0.5 : 1,
              cursor: (!aceito || !documentosValidos || enviando) ? 'not-allowed' : 'pointer'
            }}
          >
            {enviando ? 'Enviando...' : 'Confirmar Aceite do Orçamento'}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    padding: '40px 16px',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
  },
  card: {
    background: '#fff',
    borderRadius: 16,
    boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
    maxWidth: 800,
    width: '100%',
    overflow: 'hidden'
  },
  header: {
    background: 'linear-gradient(135deg, rgb(235, 133, 0) 0%, rgb(208, 116, 0) 100%)',
    padding: '32px 40px',
    color: '#fff',
    textAlign: 'center'
  },
  headerTitle: {
    margin: 0,
    fontSize: 28,
    fontWeight: 700,
    letterSpacing: 0.5
  },
  headerSubtitle: {
    margin: '8px 0 0',
    fontSize: 16,
    opacity: 0.9
  },
  section: {
    padding: '24px 40px'
  },
  sectionTitle: {
    color: 'rgb(235, 133, 0)',
    fontSize: 18,
    fontWeight: 700,
    marginTop: 0,
    marginBottom: 16,
    paddingBottom: 8,
    borderBottom: '2px solid #ffe0b2'
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px 24px'
  },
  infoItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4
  },
  infoLabel: {
    fontSize: 13,
    color: '#888',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.5
  },
  infoValue: {
    fontSize: 15,
    color: '#333',
    fontWeight: 500
  },
  descricaoBox: {
    marginTop: 16,
    background: '#f8f9fa',
    borderRadius: 8,
    padding: '12px 16px',
    borderLeft: '3px solid rgb(235, 133, 0)',
    fontSize: 14,
    color: '#555'
  },
  tabelaContainer: {
    overflowX: 'auto'
  },
  tabela: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 14
  },
  th: {
    textAlign: 'left',
    padding: '10px 12px',
    background: '#fff3e0',
    color: 'rgb(184, 98, 0)',
    fontWeight: 700,
    fontSize: 13,
    borderBottom: '2px solid #ffe0b2'
  },
  thRight: {
    textAlign: 'right',
    padding: '10px 12px',
    background: '#fff3e0',
    color: 'rgb(184, 98, 0)',
    fontWeight: 700,
    fontSize: 13,
    borderBottom: '2px solid #ffe0b2'
  },
  td: {
    padding: '10px 12px',
    borderBottom: '1px solid #f0f0f0',
    color: '#444'
  },
  tdCenter: {
    padding: '10px 12px',
    borderBottom: '1px solid #f0f0f0',
    color: '#444',
    textAlign: 'center'
  },
  tdRight: {
    padding: '10px 12px',
    borderBottom: '1px solid #f0f0f0',
    color: '#2e7d32',
    fontWeight: 600,
    textAlign: 'right'
  },
  trEven: {
    background: '#fafafa'
  },
  valoresBox: {
    background: '#f8f9fa',
    borderRadius: 8,
    padding: '16px 20px'
  },
  valorLinha: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 0',
    fontSize: 15,
    color: '#555'
  },
  valorTotal: {
    borderTop: '2px solid #ddd',
    marginTop: 8,
    paddingTop: 12,
    fontSize: 20,
    fontWeight: 700,
    color: '#333'
  },
  condicoesList: {
    listStyleType: 'none',
    padding: 0,
    margin: 0
  },
  condicaoItem: {
    padding: '10px 16px',
    background: '#f8f9fa',
    borderRadius: 6,
    marginBottom: 8,
    borderLeft: '3px solid rgb(235, 133, 0)',
    fontSize: 14,
    color: '#444'
  },
  separador: {
    border: 'none',
    borderTop: '2px dashed #e0e0e0',
    margin: '8px 40px'
  },
  aceiteSection: {
    padding: '24px 40px 40px',
    background: '#fffbf0'
  },
  aceiteTitulo: {
    color: 'rgb(184, 98, 0)',
    fontSize: 20,
    fontWeight: 700,
    marginTop: 0,
    marginBottom: 20,
    textAlign: 'center'
  },
  campoNome: {
    marginBottom: 20
  },
  label: {
    display: 'block',
    marginBottom: 8,
    fontWeight: 600,
    color: '#555',
    fontSize: 14
  },
  input: {
    width: '100%',
    padding: '14px 16px',
    border: '2px solid #ddd',
    borderRadius: 8,
    fontSize: 16,
    outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box',
    fontFamily: 'inherit'
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    cursor: 'pointer',
    padding: '16px',
    background: '#fff',
    border: '2px solid #ffe0b2',
    borderRadius: 8,
    marginBottom: 24
  },
  checkbox: {
    width: 22,
    height: 22,
    marginTop: 2,
    cursor: 'pointer',
    accentColor: 'rgb(235, 133, 0)',
    flexShrink: 0
  },
  checkboxText: {
    fontSize: 15,
    color: '#444',
    lineHeight: 1.5
  },
  botaoConfirmar: {
    width: '100%',
    padding: '16px 24px',
    background: 'linear-gradient(135deg, rgb(235, 133, 0) 0%, rgb(208, 116, 0) 100%)',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 18,
    fontWeight: 700,
    boxShadow: '0 4px 16px rgba(235, 133, 0, 0.3)',
    transition: 'all 0.2s'
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 40px'
  },
  spinner: {
    width: 48,
    height: 48,
    border: '4px solid #ffe0b2',
    borderTop: '4px solid rgb(235, 133, 0)',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 40px',
    textAlign: 'center'
  },
  successContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 40px',
    textAlign: 'center'
  }
};
